<template>
    <div class="info-message" :class="{ 'hidden': isHidden }">
        <Card>
            <template #title>
                <h3>{{ title }}</h3>
            </template>
            <template #content>
                <slot></slot>
            </template>
        </Card>
        <Button
            v-if="showToggle"
            class="info-message-toggle"
            :icon="`pi pi-${isHidden ? 'info-circle' : 'minus'}`"
            text
            rounded
            aria-label="Toggle info message"
            v-tooltip.left="isHidden ? 'Show info' : 'Hide message'"
            @click="toggleMessage"
        />
    </div>
</template>

<script setup lang="ts">
import Button from "primevue/button";
import Card from "primevue/card";
import { onMounted, ref, toRefs, watch } from "vue";

const props = defineProps<{
    title: string;
    storageKey: string;
    showToggle?: boolean;
    forceShow?: boolean;
}>();

const { forceShow } = toRefs(props);

const isHidden = ref(false);
const userPreference = ref(false); // Track user's original preference

onMounted(() => {
    try {
        const shouldHide = !!(
            localStorage.getItem(props.storageKey) === "hidden"
        );
        isHidden.value = shouldHide;
        userPreference.value = shouldHide;
    } catch (e) {
        console.warn("Failed to access localStorage", e);
        isHidden.value = false;
        userPreference.value = false;
    }
});

// Handle forceShow changes
watch(
    forceShow,
    (newForceShow) => {
        if (newForceShow) {
            // Force show the message regardless of user preference
            isHidden.value = false;
        } else {
            // Restore user's original preference when no longer forcing
            isHidden.value = userPreference.value;
        }
    },
    { immediate: true }
);

// Methods to control visibility
const toggleMessage = () => {
    isHidden.value = !isHidden.value;
    userPreference.value = isHidden.value; // Update user preference
    try {
        localStorage.setItem(
            props.storageKey,
            isHidden.value ? "hidden" : "visible"
        );
    } catch (e) {
        console.warn("Failed to save to localStorage", e);
    }
};
</script>

<style scoped lang="scss">
@keyframes shine {
    to {
        background-position: 200% center;
    }
}

.info-message {
    --info-message-margin: calc(var(--page-margin) * 0.5);

    position: relative;
    height: fit-content;
    max-height: 100%;
    transition: 0.3s ease;
    overflow: hidden;
    margin: calc(var(--info-message-margin) * -1);
    margin-top: 0;
    margin-bottom: var(--info-message-margin);
    padding: var(--stekz-border-width);

    &:not(.hidden)::after {
        content: " ";
        position: absolute;
        top: 0;
        left: 0;
        z-index: -9;
        display: block;
        height: 100%;
        width: 100%;
        background: linear-gradient(
            -45deg,
            var(--surface-background-mild) 20%,
            var(--surface-background-mild) 40%,
            var(--stekz-primary-background) 50%,
            var(--stekz-primary-background) 55%,
            var(--surface-background-mild) 70%,
            var(--surface-background-mild) 100%
        );
        background-size: 200% auto;
        animation: shine 5s linear infinite reverse;
    }

    .info-message-toggle {
        position: absolute !important;
        right: 1em;
        top: 1em;
        transition: 0.3s ease;
    }

    &.hidden {
        max-height: 0;
        margin: 0;
        overflow: visible;

        .info-message-toggle {
            top: calc(var(--info-message-margin) * -1);
            right: calc(var(--page-margin) * -1);
        }

        .p-card {
            opacity: 0;
            pointer-events: none;
        }
    }

    .p-card {
        transition: 0.3s ease;
        background-color: var(--surface-background-mild);

        opacity: 1;
        overflow: hidden;

        :deep(p) {
            margin-top: 0;

            &:last-child {
                margin-bottom: 0;
            }
        }
    }
}
</style>
